import { AUDIT_ACTION_LABELS, formatBytes, ROLE_LABELS } from "@cva/shared";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Ban, LogOut, Search, ShieldCheck, ShieldOff, Undo2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardHeader, Skeleton } from "../../components/ui/card";
import { ConfirmDialog } from "../../components/ui/confirm";
import { Dialog, DialogContent, DialogFooter } from "../../components/ui/dialog";
import { Field, Input, Select, Textarea } from "../../components/ui/input";
import { client, orpc } from "../../lib/api";
import { useSession } from "../../lib/session";
import { errorMessage, formatDate, formatDateTime, formatRelative } from "../../lib/utils";
import { AdminPageHeader, RoleBadges } from "./layout";

const PAGE = 50;
const FILTERS = [
  { id: "all", label: "Alle" },
  { id: "admins", label: "Admins" },
  { id: "banned", label: "Gesperrt" },
  { id: "unverified", label: "E-Mail unbestätigt" },
  { id: "no-org", label: "Ohne Organisation" },
] as const;

export function AdminUsersPage() {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["id"]>("all");
  const [offset, setOffset] = useState(0);
  const users = useQuery({
    ...orpc.admin.users.list.queryOptions({ input: { query, filter, limit: PAGE, offset } }),
    placeholderData: keepPreviousData,
  });
  const total = users.data?.total ?? 0;

  return (
    <>
      <AdminPageHeader title="Nutzer:innen" description={`${total} Konten`} />
      <div className="mb-4 flex flex-wrap gap-2">
        <div className="relative min-w-64 flex-1">
          <Search className="absolute top-2.5 left-3 size-4 text-zinc-400" />
          <Input
            className="pl-9"
            placeholder="Name oder E-Mail"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOffset(0);
            }}
          />
        </div>
        <Select
          className="w-52"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value as typeof filter);
            setOffset(0);
          }}
        >
          {FILTERS.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </Select>
      </div>
      <Card>
        {users.isLoading ? (
          <Skeleton className="m-5 h-40" />
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-100 text-left text-xs text-zinc-500">
              <tr>
                <th className="px-5 py-2.5 font-medium">Konto</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5 text-right font-medium">Orgs</th>
                <th className="px-3 py-2.5 text-right font-medium">Speicher</th>
                <th className="px-3 py-2.5 font-medium">Zuletzt aktiv</th>
                <th className="px-5 py-2.5 font-medium">Registriert</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {users.data?.items.map((user) => (
                <tr key={user.id} className="hover:bg-zinc-50">
                  <td className="px-5 py-2.5">
                    <Link
                      to="/admin/users/$userId"
                      params={{ userId: user.id }}
                      className="block font-medium text-zinc-900 hover:text-brand-700"
                    >
                      {user.name}
                    </Link>
                    <span className="text-xs text-zinc-500">{user.email}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <RoleBadges user={user} />
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{user.orgCount}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {formatBytes(user.storageBytes)}
                  </td>
                  <td className="px-3 py-2.5 text-zinc-600">
                    {user.lastActiveAt ? formatRelative(user.lastActiveAt) : "–"}
                  </td>
                  <td className="px-5 py-2.5 text-zinc-600">{formatDate(user.createdAt)}</td>
                </tr>
              ))}
              {users.data?.items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-zinc-500">
                    Keine Treffer
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </Card>
      <Pager offset={offset} total={total} onChange={setOffset} />
    </>
  );
}

export function Pager({
  offset,
  total,
  onChange,
  page = PAGE,
}: {
  offset: number;
  total: number;
  onChange: (offset: number) => void;
  page?: number;
}) {
  if (total <= page) return null;
  return (
    <div className="mt-4 flex items-center justify-end gap-2 text-sm text-zinc-600">
      <span>
        {offset + 1}–{Math.min(total, offset + page)} von {total}
      </span>
      <Button
        variant="secondary"
        size="sm"
        disabled={offset === 0}
        onClick={() => onChange(Math.max(0, offset - page))}
      >
        Zurück
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabled={offset + page >= total}
        onClick={() => onChange(offset + page)}
      >
        Weiter
      </Button>
    </div>
  );
}

function BanDialog({
  userId,
  open,
  onOpenChange,
}: {
  userId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [days, setDays] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await client.admin.users.ban({ userId, reason, days: days ? Number(days) : null });
      toast.success("Konto gesperrt, alle Sitzungen beendet");
      await queryClient.invalidateQueries({ queryKey: orpc.admin.users.key() });
      onOpenChange(false);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Konto sperren"
        description="Die Person wird überall abgemeldet (auch verbundene Apps) und kann sich nicht mehr anmelden. Inhalte bleiben erhalten."
      >
        <form onSubmit={submit} className="space-y-4">
          <Field label="Grund (nur für Admins sichtbar, landet im Audit-Log)">
            <Textarea
              required
              minLength={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <Field label="Dauer">
            <Select value={days} onChange={(e) => setDays(e.target.value)}>
              <option value="">Bis zur Aufhebung</option>
              <option value="1">1 Tag</option>
              <option value="7">7 Tage</option>
              <option value="30">30 Tage</option>
            </Select>
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="danger" loading={busy}>
              Sperren
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AuditList({
  items,
}: {
  items: {
    id: string;
    action: string;
    actorEmail: string;
    createdAt: Date;
    details: Record<string, unknown>;
  }[];
}) {
  if (items.length === 0) return <p className="px-5 py-6 text-sm text-zinc-500">Keine Einträge.</p>;
  return (
    <ul className="divide-y divide-zinc-100">
      {items.map((entry) => (
        <li key={entry.id} className="flex flex-wrap items-baseline gap-x-3 px-5 py-2.5 text-sm">
          <span className="font-medium text-zinc-900">
            {AUDIT_ACTION_LABELS[entry.action] ?? entry.action}
          </span>
          <span className="text-zinc-500">von {entry.actorEmail}</span>
          <span className="ml-auto text-xs text-zinc-400">{formatDateTime(entry.createdAt)}</span>
        </li>
      ))}
    </ul>
  );
}

export function AdminUserPage() {
  const { userId } = useParams({ strict: false }) as { userId: string };
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const user = useQuery(orpc.admin.users.get.queryOptions({ input: { userId } }));
  const [banning, setBanning] = useState(false);
  const [confirm, setConfirm] = useState<"sessions" | "grant" | "revoke" | "unban" | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: orpc.admin.users.key() });
  const self = session?.user.id === userId;

  if (!user.data) {
    return user.error ? (
      <p className="text-red-600">{errorMessage(user.error)}</p>
    ) : (
      <Skeleton className="h-96" />
    );
  }
  const u = user.data;

  async function run(action: () => Promise<unknown>, success: string) {
    try {
      await action();
      toast.success(success);
      await refresh();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }

  return (
    <>
      <Link
        to="/admin/users"
        className="mb-3 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900"
      >
        <ArrowLeft className="size-4" /> Alle Konten
      </Link>
      <AdminPageHeader
        title={u.name}
        description={
          <span className="flex flex-wrap items-center gap-2">
            {u.email} <RoleBadges user={u} />
          </span>
        }
        actions={
          <>
            {!self ? (
              <Button variant="secondary" onClick={() => setConfirm("sessions")}>
                <LogOut /> Sitzungen beenden
              </Button>
            ) : null}
            {u.role === "admin" ? (
              <Button variant="secondary" onClick={() => setConfirm("revoke")}>
                <ShieldOff /> Admin entziehen
              </Button>
            ) : !u.banned ? (
              <Button variant="secondary" onClick={() => setConfirm("grant")}>
                <ShieldCheck /> Zum Admin machen
              </Button>
            ) : null}
            {u.banned ? (
              <Button variant="secondary" onClick={() => setConfirm("unban")}>
                <Undo2 /> Sperre aufheben
              </Button>
            ) : !self && u.role !== "admin" ? (
              <Button variant="danger" onClick={() => setBanning(true)}>
                <Ban /> Sperren
              </Button>
            ) : null}
          </>
        }
      />

      {u.banned ? (
        <Card className="mb-6 border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Gesperrt{u.banExpires ? ` bis ${formatDateTime(u.banExpires)}` : ""}: {u.banReason}
        </Card>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Konto" />
          <dl className="grid grid-cols-[10rem_1fr] gap-y-2 p-5 text-sm">
            <dt className="text-zinc-500">Registriert</dt>
            <dd>{formatDateTime(u.createdAt)}</dd>
            <dt className="text-zinc-500">Zuletzt aktiv</dt>
            <dd>{u.lastActiveAt ? formatDateTime(u.lastActiveAt) : "–"}</dd>
            <dt className="text-zinc-500">Anmeldung</dt>
            <dd>{u.loginMethods.join(", ") || "–"}</dd>
            <dt className="text-zinc-500">E-Mail bestätigt</dt>
            <dd>{u.emailVerified ? "Ja" : "Nein"}</dd>
            <dt className="text-zinc-500">2FA</dt>
            <dd>{u.twoFactorEnabled ? "Aktiv" : "Aus"}</dd>
            <dt className="text-zinc-500">Speicher</dt>
            <dd>{formatBytes(u.storageBytes)}</dd>
          </dl>
        </Card>
        <Card>
          <CardHeader title="Organisationen" />
          {u.memberships.length === 0 ? (
            <p className="px-5 py-6 text-sm text-zinc-500">Keine.</p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {u.memberships.map((m) => (
                <li key={m.orgId} className="flex items-center justify-between px-5 py-2.5 text-sm">
                  <Link
                    to="/admin/orgs/$orgId"
                    params={{ orgId: m.orgId }}
                    className="font-medium hover:text-brand-700"
                  >
                    {m.orgName}
                  </Link>
                  <span className="text-zinc-500">
                    {ROLE_LABELS[m.role as keyof typeof ROLE_LABELS] ?? m.role} · seit{" "}
                    {formatDate(m.joinedAt)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card>
          <CardHeader
            title="Aktive Sitzungen"
            description="Browser-Anmeldungen, die noch nicht abgelaufen sind."
          />
          {u.sessions.length === 0 ? (
            <p className="px-5 py-6 text-sm text-zinc-500">Keine.</p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {u.sessions.map((s) => (
                <li key={s.id} className="px-5 py-2.5 text-sm">
                  <p className="truncate text-zinc-900">{s.userAgent ?? "Unbekannter Browser"}</p>
                  <p className="text-xs text-zinc-500">
                    {s.ipAddress ?? "IP unbekannt"} · angemeldet {formatDateTime(s.createdAt)} ·
                    aktiv {formatRelative(s.updatedAt)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card>
          <CardHeader title="Verbundene Apps" />
          {u.connectedApps.length === 0 ? (
            <p className="px-5 py-6 text-sm text-zinc-500">Keine.</p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {u.connectedApps.map((app) => (
                <li key={app.clientId} className="flex justify-between px-5 py-2.5 text-sm">
                  <span>{app.name ?? app.clientId}</span>
                  <span className="text-zinc-500">seit {formatDate(app.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader
          title="Verlauf"
          description="Admin-Aktionen zu diesem Konto oder von diesem Konto."
        />
        <AuditList items={u.audit} />
      </Card>

      <BanDialog userId={userId} open={banning} onOpenChange={setBanning} />
      <ConfirmDialog
        open={confirm === "sessions"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title="Alle Sitzungen beenden?"
        description="Die Person wird in allen Browsern abgemeldet und muss sich neu anmelden."
        confirmLabel="Beenden"
        onConfirm={() =>
          run(async () => {
            const result = await client.admin.users.revokeSessions({ userId });
            toast.message(`${result.revoked} Sitzung(en) beendet`);
          }, "Erledigt")
        }
      />
      <ConfirmDialog
        open={confirm === "grant"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title="Admin-Rechte vergeben?"
        description={`${u.name} kann danach alle Konten und Organisationen einsehen und verwalten. Alle Admins werden benachrichtigt. Für den Zugang braucht die Person 2FA.`}
        confirmLabel="Admin-Rechte vergeben"
        onConfirm={() =>
          run(() => client.admin.users.setAdmin({ userId, admin: true }), "Admin-Rechte vergeben")
        }
      />
      <ConfirmDialog
        open={confirm === "revoke"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title="Admin-Rechte entziehen?"
        description={self ? "Du verlierst damit selbst den Zugang zum Admin-Bereich." : undefined}
        confirmLabel="Entziehen"
        danger
        onConfirm={() =>
          run(() => client.admin.users.setAdmin({ userId, admin: false }), "Admin-Rechte entzogen")
        }
      />
      <ConfirmDialog
        open={confirm === "unban"}
        onOpenChange={(open) => !open && setConfirm(null)}
        title="Sperre aufheben?"
        confirmLabel="Aufheben"
        onConfirm={() => run(() => client.admin.users.unban({ userId }), "Sperre aufgehoben")}
      />
    </>
  );
}
